import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SIDEBAR_LINKS } from "@/constants/nav";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, LogOut, ShieldCheck, Search, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import BrandLogo from "@/components/BrandLogo";

function NavList({ onNavigate }) {
  const loc = useLocation();
  const { isAdmin } = useAuth();
  const links = [...SIDEBAR_LINKS];
  return (
    <nav className="flex flex-col gap-1 p-3" data-testid="sidebar-nav">
      {links.map((l) => {
        const active = loc.pathname === l.to;
        const Icon = l.icon;
        return (
          <Link
            key={l.to}
            to={l.to}
            onClick={onNavigate}
            data-testid={`nav-${l.title.toLowerCase().replace(/\s+/g, "-")}`}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              active ? "bg-mlily-blue text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
            )}
          >
            <Icon className="w-4 h-4" /> {l.title}
          </Link>
        );
      })}
      {isAdmin && (
        <Link
          to="/admin"
          onClick={onNavigate}
          data-testid="nav-admin"
          className={cn(
            "mt-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors",
            loc.pathname.startsWith("/admin") ? "bg-slate-900 text-white" : "text-slate-900 bg-slate-100 hover:bg-slate-200"
          )}
        >
          <Settings className="w-4 h-4" /> Admin Area
        </Link>
      )}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/troubleshooting?q=${encodeURIComponent(q.trim())}`);
  };

  const Brand = (
    <Link to="/" className="flex items-center px-4 py-5 border-b border-slate-100" data-testid="brand-link">
      <BrandLogo size={38} />
    </Link>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 z-30">
        {Brand}
        <div className="flex-1 overflow-y-auto">
          <NavList />
        </div>
        <div className="p-3 border-t border-slate-100">
          <div className="px-3 py-2 text-xs text-slate-500 truncate">{user?.email}</div>
          <Button variant="ghost" className="w-full justify-start gap-2 text-slate-600" onClick={logout} data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200">
          <div className="flex items-center gap-3 px-4 h-16">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72">
                {Brand}
                <NavList onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <form onSubmit={doSearch} className="flex-1 max-w-xl relative" data-testid="global-search-form">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="global-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search troubleshooting (e.g. remote not pairing)"
                className="w-full pl-9 pr-3 h-10 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-mlily-blue"
              />
            </form>
            <div className="hidden sm:flex items-center gap-2 rounded-full bg-slate-100 pl-3 pr-1 py-1">
              <span className="text-xs font-medium text-slate-700">{user?.name}</span>
              <span className={cn("text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full",
                user?.role === "admin" ? "bg-mlily-blue text-white" : "bg-emerald-100 text-emerald-700")}>
                {user?.role}
              </span>
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{children}</main>
        <footer className="border-t border-slate-200 mt-8 py-6 px-4 sm:px-8" data-testid="footer">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <BrandLogo size={28} />
            <p className="text-xs text-slate-400">Internal use only · © {new Date().getFullYear()} MLILY · Authorised staff access</p>
          </div>
        </footer>
      </div>
    </div>
  );
}
